import { useCallback, useEffect, useRef, useState } from 'react'
import { LINGER_MS } from './taskRules'
import { LEAVE_MS } from '../ui/Row'

/**
 * D1, as a hook for the phone's four list components: a row just finished
 * stays drawn open and struck through for `LINGER_MS` (`lingering`), then
 * collapses through the Row's `leaving` prop for `LEAVE_MS`, then is
 * forgotten here and the fold draws it. Two sets of ids in component state,
 * never a column — the write happened at the tap. `cancel` is for an untick
 * inside the linger and for a write the server refused.
 */
export function useLinger() {
  const [lingering, setLingering] = useState<ReadonlySet<string>>(() => new Set())
  const [leaving, setLeaving] = useState<ReadonlySet<string>>(() => new Set())
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>[]>())

  const cancel = useCallback((id: string) => {
    for (const t of timers.current.get(id) ?? []) clearTimeout(t)
    timers.current.delete(id)
    setLingering(prev => { if (!prev.has(id)) return prev; const n = new Set(prev); n.delete(id); return n })
    setLeaving(prev => { if (!prev.has(id)) return prev; const n = new Set(prev); n.delete(id); return n })
  }, [])

  const start = useCallback((id: string) => {
    cancel(id)
    setLingering(prev => new Set(prev).add(id))
    const t1 = setTimeout(() => setLeaving(prev => new Set(prev).add(id)), LINGER_MS)
    const t2 = setTimeout(() => cancel(id), LINGER_MS + LEAVE_MS)
    timers.current.set(id, [t1, t2])
  }, [cancel])

  const cancelAll = useCallback(() => { for (const id of [...timers.current.keys()]) cancel(id) }, [cancel])

  useEffect(() => () => { for (const ts of timers.current.values()) for (const t of ts) clearTimeout(t) }, [])

  return { lingering, leaving, start, cancel, cancelAll }
}
